import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Tabs,
  Tab,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TableContainer,
  Paper,
  TextField,
  Chip,
  Pagination,
  CircularProgress,
  Alert,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Card,
  CardContent,
  Grid,
  Button,
  InputAdornment,
  List,
  ListItem,
  ListItemText,
  Link,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import GavelIcon from '@mui/icons-material/Gavel';
import ArticleIcon from '@mui/icons-material/Article';
import AccountTreeIcon from '@mui/icons-material/AccountTree';
import RuleIcon from '@mui/icons-material/Rule';
import LibraryBooksIcon from '@mui/icons-material/LibraryBooks';
import { useNavigate } from 'react-router-dom';
import pandoraAdminService from '../../../services/pandoraAdminService';

const PAGE_SIZE = 20;

const DOMAIN_CHIP_COLORS: Record<string, 'primary' | 'success' | 'warning'> = {
  estrutural: 'primary',
  teorico: 'success',
  factual: 'warning',
};

function truncate(str: string | null | undefined, len: number): string {
  if (!str) return '';
  return str.length <= len ? str : str.slice(0, len) + '...';
}

interface TabPanelProps {
  children: React.ReactNode;
  value: number;
  index: number;
}

function TabPanel({ children, value, index }: TabPanelProps) {
  return (
    <div role="tabpanel" hidden={value !== index}>
      {value === index && <Box sx={{ pt: 2 }}>{children}</Box>}
    </div>
  );
}

export default function PandoraValidatedContentPage() {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState(0);
  const [summary, setSummary] = useState<{
    glossary: number;
    jurisprudence: number;
    legislation: number;
    paragraphs: number;
    rules: number;
  } | null>(null);
  const [summaryLoading, setSummaryLoading] = useState(true);
  const [summaryError, setSummaryError] = useState<string | null>(null);

  // Tab 1 - Verbetes
  const [verbetesSearch, setVerbetesSearch] = useState('');
  const [verbetesDomain, setVerbetesDomain] = useState('');
  const [verbetesPage, setVerbetesPage] = useState(1);
  const [verbetesData, setVerbetesData] = useState<{
    items: any[];
    total: number;
    page: number;
    page_size: number;
  } | null>(null);
  const [verbetesLoading, setVerbetesLoading] = useState(false);
  const [verbetesError, setVerbetesError] = useState<string | null>(null);

  // Tab 2 - Jurisprudencias
  const [jurisSearch, setJurisSearch] = useState('');
  const [jurisCourt, setJurisCourt] = useState('');
  const [jurisDomain, setJurisDomain] = useState('');
  const [jurisPage, setJurisPage] = useState(1);
  const [jurisData, setJurisData] = useState<{
    items: any[];
    total: number;
    page: number;
    page_size: number;
  } | null>(null);
  const [jurisLoading, setJurisLoading] = useState(false);
  const [jurisError, setJurisError] = useState<string | null>(null);

  // Tab 3 - Legislacao
  const [legSearch, setLegSearch] = useState('');
  const [legArea, setLegArea] = useState('');
  const [legPage, setLegPage] = useState(1);
  const [legData, setLegData] = useState<{
    items: any[];
    total: number;
    page: number;
    page_size: number;
  } | null>(null);
  const [legLoading, setLegLoading] = useState(false);
  const [legError, setLegError] = useState<string | null>(null);

  // Tab 4 - Paragrafos
  const [paraDomain, setParaDomain] = useState<string | null>(null);
  const [paraClass, setParaClass] = useState('');
  const [paraSearch, setParaSearch] = useState('');
  const [paraPage, setParaPage] = useState(1);
  const [paraData, setParaData] = useState<{
    items: any[];
    total: number;
    page: number;
    page_size: number;
    by_domain?: Record<string, number>;
  } | null>(null);
  const [paraLoading, setParaLoading] = useState(false);
  const [paraError, setParaError] = useState<string | null>(null);

  // Tab 5 - Regras
  const [rulesCategory, setRulesCategory] = useState('');
  const [rulesPage, setRulesPage] = useState(1);
  const [rulesData, setRulesData] = useState<{
    items: any[];
    total: number;
    page: number;
    page_size: number;
  } | null>(null);
  const [rulesLoading, setRulesLoading] = useState(false);
  const [rulesError, setRulesError] = useState<string | null>(null);

  const loadSummary = useCallback(async () => {
    try {
      setSummaryLoading(true);
      setSummaryError(null);
      const data = await pandoraAdminService.getValidatedSummary();
      setSummary(data);
    } catch (err) {
      console.error('Erro ao carregar resumo:', err);
      setSummaryError('Não foi possível carregar o resumo.');
    } finally {
      setSummaryLoading(false);
    }
  }, []);

  useEffect(() => {
    loadSummary();
  }, [loadSummary]);

  const loadVerbetes = useCallback(async () => {
    if (activeTab !== 0) return;
    try {
      setVerbetesLoading(true);
      setVerbetesError(null);
      const data = await pandoraAdminService.getValidatedGlossary({
        search: verbetesSearch || undefined,
        legal_domain: verbetesDomain || undefined,
        page: verbetesPage,
        page_size: PAGE_SIZE,
      });
      setVerbetesData(data);
    } catch (err) {
      console.error('Erro ao carregar verbetes:', err);
      setVerbetesError('Não foi possível carregar os verbetes.');
    } finally {
      setVerbetesLoading(false);
    }
  }, [activeTab, verbetesSearch, verbetesDomain, verbetesPage]);

  const loadJuris = useCallback(async () => {
    if (activeTab !== 1) return;
    try {
      setJurisLoading(true);
      setJurisError(null);
      const data = await pandoraAdminService.getValidatedJurisprudence({
        search: jurisSearch || undefined,
        court_name: jurisCourt || undefined,
        legal_domain: jurisDomain || undefined,
        page: jurisPage,
        page_size: PAGE_SIZE,
      });
      setJurisData(data);
    } catch (err) {
      console.error('Erro ao carregar jurisprudencias:', err);
      setJurisError('Não foi possível carregar as jurisprudências.');
    } finally {
      setJurisLoading(false);
    }
  }, [activeTab, jurisSearch, jurisCourt, jurisDomain, jurisPage]);

  const loadLegislation = useCallback(async () => {
    if (activeTab !== 2) return;
    try {
      setLegLoading(true);
      setLegError(null);
      const data = await pandoraAdminService.getValidatedLegislation({
        search: legSearch || undefined,
        area: legArea || undefined,
        page: legPage,
        page_size: PAGE_SIZE,
      });
      setLegData(data);
    } catch (err) {
      console.error('Erro ao carregar legislacao:', err);
      setLegError('Não foi possível carregar a legislação.');
    } finally {
      setLegLoading(false);
    }
  }, [activeTab, legSearch, legArea, legPage]);

  const loadParagraphs = useCallback(async () => {
    if (activeTab !== 3) return;
    try {
      setParaLoading(true);
      setParaError(null);
      const data = await pandoraAdminService.getValidatedParagraphs({
        tag_domain: paraDomain || undefined,
        tag_class: paraClass || undefined,
        search: paraSearch || undefined,
        page: paraPage,
        page_size: PAGE_SIZE,
      });
      setParaData(data);
    } catch (err) {
      console.error('Erro ao carregar paragrafos:', err);
      setParaError('Não foi possível carregar os parágrafos.');
    } finally {
      setParaLoading(false);
    }
  }, [activeTab, paraDomain, paraClass, paraSearch, paraPage]);

  const loadRules = useCallback(async () => {
    if (activeTab !== 4) return;
    try {
      setRulesLoading(true);
      setRulesError(null);
      const data = await pandoraAdminService.getRules({
        category: rulesCategory || undefined,
        page: rulesPage,
        page_size: PAGE_SIZE,
      });
      setRulesData(data);
    } catch (err) {
      console.error('Erro ao carregar regras:', err);
      setRulesError('Não foi possível carregar as regras.');
    } finally {
      setRulesLoading(false);
    }
  }, [activeTab, rulesCategory, rulesPage]);

  useEffect(() => {
    if (activeTab === 0) loadVerbetes();
    else if (activeTab === 1) loadJuris();
    else if (activeTab === 2) loadLegislation();
    else if (activeTab === 3) loadParagraphs();
    else if (activeTab === 4) loadRules();
  }, [activeTab, loadVerbetes, loadJuris, loadLegislation, loadParagraphs, loadRules]);

  const handleTabChange = (_: React.SyntheticEvent, v: number) => {
    setActiveTab(v);
  };

  const totalPages = (total: number) => Math.max(1, Math.ceil(total / PAGE_SIZE));

  return (
    <Box sx={{ width: '100%', p: 3 }}>
      <Box sx={{ mb: 3 }}>
        <Typography variant="h4" gutterBottom>
          Conteúdo Validado
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Visualização e consulta de todo o conteúdo validado do Pandora.
        </Typography>
      </Box>

      {summaryError && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {summaryError}
        </Alert>
      )}

      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={2}>
          <Card>
            <CardContent>
              <Typography color="text.secondary" gutterBottom variant="overline">
                Verbetes
              </Typography>
              <Typography variant="h5">
                {summaryLoading ? '...' : summary?.glossary ?? 0}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={2}>
          <Card>
            <CardContent>
              <Typography color="text.secondary" gutterBottom variant="overline">
                Jurisprudencias
              </Typography>
              <Typography variant="h5">
                {summaryLoading ? '...' : summary?.jurisprudence ?? 0}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={2}>
          <Card>
            <CardContent>
              <Typography color="text.secondary" gutterBottom variant="overline">
                Legislacao
              </Typography>
              <Typography variant="h5">
                {summaryLoading ? '...' : summary?.legislation ?? 0}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={2}>
          <Card>
            <CardContent>
              <Typography color="text.secondary" gutterBottom variant="overline">
                Paragrafos
              </Typography>
              <Typography variant="h5">
                {summaryLoading ? '...' : summary?.paragraphs ?? 0}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={2}>
          <Card>
            <CardContent>
              <Typography color="text.secondary" gutterBottom variant="overline">
                Regras
              </Typography>
              <Typography variant="h5">
                {summaryLoading ? '...' : summary?.rules ?? 0}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <Tabs value={activeTab} onChange={handleTabChange} sx={{ borderBottom: 1, borderColor: 'divider' }}>
        <Tab icon={<LibraryBooksIcon />} iconPosition="start" label="Verbetes" />
        <Tab icon={<GavelIcon />} iconPosition="start" label="Jurisprudencias" />
        <Tab icon={<ArticleIcon />} iconPosition="start" label="Legislacao" />
        <Tab icon={<AccountTreeIcon />} iconPosition="start" label="Paragrafos" />
        <Tab icon={<RuleIcon />} iconPosition="start" label="Regras" />
      </Tabs>

      {/* Tab 1 - Verbetes */}
      <TabPanel value={activeTab} index={0}>
        <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap' }}>
          <TextField
            placeholder="Buscar termo..."
            size="small"
            value={verbetesSearch}
            onChange={(e) => setVerbetesSearch(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
            sx={{ minWidth: 200 }}
          />
          <TextField
            placeholder="Área (domínio)"
            size="small"
            value={verbetesDomain}
            onChange={(e) => setVerbetesDomain(e.target.value)}
            sx={{ minWidth: 180 }}
          />
          <Button variant="contained" onClick={loadVerbetes} disabled={verbetesLoading}>
            {verbetesLoading ? <CircularProgress size={24} /> : 'Buscar'}
          </Button>
        </Box>
        {verbetesError && (
          <Alert severity="error" sx={{ mb: 2 }}>{verbetesError}</Alert>
        )}
        {verbetesLoading ? (
          <Box display="flex" justifyContent="center" py={4}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            <TableContainer component={Paper}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Termo</TableCell>
                    <TableCell>Definição</TableCell>
                    <TableCell>Definição Simples</TableCell>
                    <TableCell>Área</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {(verbetesData?.items ?? []).map((row: any) => (
                    <TableRow key={row.id}>
                      <TableCell>{row.title}</TableCell>
                      <TableCell>{truncate(row.definition, 200)}</TableCell>
                      <TableCell>{truncate(row.simple_definition, 200)}</TableCell>
                      <TableCell>{row.legal_domain || '-'}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
            {verbetesData && verbetesData.total > PAGE_SIZE && (
              <Box display="flex" justifyContent="center" py={2}>
                <Pagination
                  count={totalPages(verbetesData.total)}
                  page={verbetesPage}
                  onChange={(_, p) => setVerbetesPage(p)}
                  color="primary"
                />
              </Box>
            )}
          </>
        )}
      </TabPanel>

      {/* Tab 2 - Jurisprudencias */}
      <TabPanel value={activeTab} index={1}>
        <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap' }}>
          <TextField
            placeholder="Buscar titulo ou ementa..."
            size="small"
            value={jurisSearch}
            onChange={(e) => setJurisSearch(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
            sx={{ minWidth: 220 }}
          />
          <TextField
            placeholder="Tribunal"
            size="small"
            value={jurisCourt}
            onChange={(e) => setJurisCourt(e.target.value)}
            sx={{ minWidth: 150 }}
          />
          <TextField
            placeholder="Área (domínio)"
            size="small"
            value={jurisDomain}
            onChange={(e) => setJurisDomain(e.target.value)}
            sx={{ minWidth: 150 }}
          />
          <Button variant="contained" onClick={loadJuris} disabled={jurisLoading}>
            {jurisLoading ? <CircularProgress size={24} /> : 'Buscar'}
          </Button>
        </Box>
        {jurisError && (
          <Alert severity="error" sx={{ mb: 2 }}>{jurisError}</Alert>
        )}
        {jurisLoading ? (
          <Box display="flex" justifyContent="center" py={4}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            <TableContainer component={Paper}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Tribunal</TableCell>
                    <TableCell>CNJ</TableCell>
                    <TableCell>Titulo</TableCell>
                    <TableCell>Ementa</TableCell>
                    <TableCell>Área</TableCell>
                    <TableCell>Score</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {(jurisData?.items ?? []).map((row: any) => (
                    <TableRow key={row.id}>
                      <TableCell>{row.court_name || '-'}</TableCell>
                      <TableCell>{row.cnj_number || '-'}</TableCell>
                      <TableCell>{truncate(row.title, 100)}</TableCell>
                      <TableCell>{truncate(row.ementa, 200)}</TableCell>
                      <TableCell>{row.legal_domain || '-'}</TableCell>
                      <TableCell>{row.quality_score ?? '-'}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
            {jurisData && jurisData.total > PAGE_SIZE && (
              <Box display="flex" justifyContent="center" py={2}>
                <Pagination
                  count={totalPages(jurisData.total)}
                  page={jurisPage}
                  onChange={(_, p) => setJurisPage(p)}
                  color="primary"
                />
              </Box>
            )}
          </>
        )}
      </TabPanel>

      {/* Tab 3 - Legislacao */}
      <TabPanel value={activeTab} index={2}>
        <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap' }}>
          <TextField
            placeholder="Buscar..."
            size="small"
            value={legSearch}
            onChange={(e) => setLegSearch(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
            sx={{ minWidth: 200 }}
          />
          <TextField
            placeholder="Área"
            size="small"
            value={legArea}
            onChange={(e) => setLegArea(e.target.value)}
            sx={{ minWidth: 150 }}
          />
          <Button variant="contained" onClick={loadLegislation} disabled={legLoading}>
            {legLoading ? <CircularProgress size={24} /> : 'Buscar'}
          </Button>
        </Box>
        {legError && (
          <Alert severity="error" sx={{ mb: 2 }}>{legError}</Alert>
        )}
        {legLoading ? (
          <Box display="flex" justifyContent="center" py={4}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            <TableContainer component={Paper}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Codigo</TableCell>
                    <TableCell>Artigo</TableCell>
                    <TableCell>Texto</TableCell>
                    <TableCell>Área</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {(legData?.items ?? []).map((row: any) => (
                    <TableRow key={row.id}>
                      <TableCell>{row.law_code || row.law_name || '-'}</TableCell>
                      <TableCell>{row.article_number || '-'}</TableCell>
                      <TableCell>{truncate(row.article_text, 200)}</TableCell>
                      <TableCell>{row.area || '-'}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
            {legData && legData.total > PAGE_SIZE && (
              <Box display="flex" justifyContent="center" py={2}>
                <Pagination
                  count={totalPages(legData.total)}
                  page={legPage}
                  onChange={(_, p) => setLegPage(p)}
                  color="primary"
                />
              </Box>
            )}
          </>
        )}
      </TabPanel>

      {/* Tab 4 - Paragrafos */}
      <TabPanel value={activeTab} index={3}>
        <Box sx={{ mb: 2 }}>
          {paraData?.by_domain && Object.keys(paraData.by_domain).length > 0 && (
            <Box sx={{ display: 'flex', gap: 1, mb: 2, flexWrap: 'wrap' }}>
              {Object.entries(paraData.by_domain).map(([domain, count]) => (
                <Chip
                  key={domain}
                  label={`${domain}: ${count}`}
                  color={DOMAIN_CHIP_COLORS[domain] ?? 'default'}
                  variant={paraDomain === domain ? 'filled' : 'outlined'}
                  onClick={() => setParaDomain(paraDomain === domain ? null : domain)}
                />
              ))}
            </Box>
          )}
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
            <FormControl size="small" sx={{ minWidth: 140 }}>
              <InputLabel>Classe</InputLabel>
              <Select
                value={paraClass}
                label="Classe"
                onChange={(e) => setParaClass(e.target.value)}
              >
                <MenuItem value="">Todas</MenuItem>
                <MenuItem value="estrutural">Estrutural</MenuItem>
                <MenuItem value="teorico">Teórico</MenuItem>
                <MenuItem value="factual">Factual</MenuItem>
              </Select>
            </FormControl>
            <TextField
              placeholder="Buscar..."
              size="small"
              value={paraSearch}
              onChange={(e) => setParaSearch(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
              sx={{ minWidth: 180 }}
            />
            <Button variant="contained" onClick={loadParagraphs} disabled={paraLoading}>
              {paraLoading ? <CircularProgress size={24} /> : 'Buscar'}
            </Button>
          </Box>
        </Box>
        {paraError && (
          <Alert severity="error" sx={{ mb: 2 }}>{paraError}</Alert>
        )}
        {paraLoading ? (
          <Box display="flex" justifyContent="center" py={4}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            <List>
              {(paraData?.items ?? []).map((p: any) => (
                <ListItem
                  key={p.id}
                  divider
                  sx={{ flexDirection: 'column', alignItems: 'flex-start' }}
                >
                  <ListItemText
                    primary={truncate(p.text, 300)}
                    secondary={
                      <Box sx={{ display: 'flex', gap: 1, mt: 1, flexWrap: 'wrap' }}>
                        {p.taxonomy_tag && (
                          <Chip
                            size="small"
                            label={p.taxonomy_tag}
                            variant="outlined"
                          />
                        )}
                        {p.confidence != null && (
                          <Chip
                            size="small"
                            label={`${Math.round(p.confidence * 100)}%`}
                            variant="outlined"
                          />
                        )}
                        {p.document_id && (
                          <Link
                            component="button"
                            variant="body2"
                            onClick={() => navigate(`/admin/pandora/documents/${p.document_id}`)}
                          >
                            Documento {p.document_id.slice(0, 8)}...
                          </Link>
                        )}
                      </Box>
                    }
                  />
                </ListItem>
              ))}
            </List>
            {paraData && paraData.total > PAGE_SIZE && (
              <Box display="flex" justifyContent="center" py={2}>
                <Pagination
                  count={totalPages(paraData.total)}
                  page={paraPage}
                  onChange={(_, p) => setParaPage(p)}
                  color="primary"
                />
              </Box>
            )}
          </>
        )}
      </TabPanel>

      {/* Tab 5 - Regras */}
      <TabPanel value={activeTab} index={4}>
        <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap' }}>
          <FormControl size="small" sx={{ minWidth: 180 }}>
            <InputLabel>Categoria</InputLabel>
            <Select
              value={rulesCategory}
              label="Categoria"
              onChange={(e) => setRulesCategory(e.target.value)}
            >
              <MenuItem value="">Todas</MenuItem>
              <MenuItem value="obrigatorio">Obrigatorio</MenuItem>
              <MenuItem value="recomendado">Recomendado</MenuItem>
              <MenuItem value="proibido">Proibido</MenuItem>
            </Select>
          </FormControl>
          <Button variant="contained" onClick={loadRules} disabled={rulesLoading}>
            {rulesLoading ? <CircularProgress size={24} /> : 'Buscar'}
          </Button>
        </Box>
        {rulesError && (
          <Alert severity="error" sx={{ mb: 2 }}>{rulesError}</Alert>
        )}
        {rulesLoading ? (
          <Box display="flex" justifyContent="center" py={4}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            <TableContainer component={Paper}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Regra</TableCell>
                    <TableCell>Termo A</TableCell>
                    <TableCell>Operador</TableCell>
                    <TableCell>Termo B</TableCell>
                    <TableCell>Categoria</TableCell>
                    <TableCell>Domínio</TableCell>
                    <TableCell>Ativo</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {(rulesData?.items ?? []).map((row: any) => (
                    <TableRow key={row.id}>
                      <TableCell>{truncate(row.raw_text, 80)}</TableCell>
                      <TableCell>{row.term_a || '-'}</TableCell>
                      <TableCell>{row.operator || '-'}</TableCell>
                      <TableCell>{row.term_b || '-'}</TableCell>
                      <TableCell>{row.category || '-'}</TableCell>
                      <TableCell>{row.legal_domain || '-'}</TableCell>
                      <TableCell>
                        <Chip
                            size="small"
                            label={row.is_active ? 'Sim' : 'Não'}
                          color={row.is_active ? 'success' : 'default'}
                          variant="outlined"
                        />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
            {rulesData && rulesData.total > PAGE_SIZE && (
              <Box display="flex" justifyContent="center" py={2}>
                <Pagination
                  count={totalPages(rulesData.total)}
                  page={rulesPage}
                  onChange={(_, p) => setRulesPage(p)}
                  color="primary"
                />
              </Box>
            )}
          </>
        )}
      </TabPanel>
    </Box>
  );
}
